/**
 * Whether a chart's x values would give plotly a year-less date axis.
 *
 * Plotly's date parser (plotly.js/src/lib/dates.js) reads a leading 2-digit
 * field as a YEAR, so a year-less "MM-DD" label becomes a date in the 2000s:
 * "09-01" -> 2009-01-01, while "09-14" (month 14) is invalid and the point is
 * dropped entirely. axis_autotype.js then types the whole axis as a date axis
 * whenever distinct date-parsing values outnumber distinct numeric ones 2:1,
 * so labels that merely sit alongside year-less ones are enough to trigger it.
 */

// Port of plotly's DATETIME_REGEXP, time suffix included: a value carrying a
// full year must be recognised as such even when it is a timestamp, or a mixed
// axis would be pinned despite plotly dating it correctly.
const PLOTLY_DATE_RE =
  /^\s*(-?\d\d\d\d|\d\d)(?:-(\d?\d)(?:-(\d?\d)(?:[ Tt](?:[01]?\d|2[0-3])(?::[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:[Zz]|[+-]\d\d(?::?\d\d)?)?)?)?)?)?\s*$/;

type PlotlyDateKind = "yearless" | "dated" | null;

function plotlyDateKind(value: unknown): PlotlyDateKind {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const match = String(value).match(PLOTLY_DATE_RE);
  if (!match) return null;
  const month = Number(match[2] ?? 1);
  const day = Number(match[3] ?? 1);
  const probe = new Date(Date.UTC(2000, month - 1, day));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return match[1].length === 2 ? "yearless" : "dated";
}

function isNumericValue(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  return typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value));
}

function hasYearlessDateAxis(values: unknown[]): boolean {
  const seen = new Set<string>();
  let yearless = 0;
  let dated = 0;
  let numeric = 0;
  for (const value of values) {
    const key = String(value);
    if (seen.has(key)) continue;
    seen.add(key);
    const kind = plotlyDateKind(value);
    if (kind === "yearless") yearless += 1;
    else if (kind === "dated") dated += 1;
    if (isNumericValue(value)) numeric += 1;
  }
  // Plotly counts every date it can parse when deciding the axis type, so a
  // full-year date alongside year-less ones does not rescue the chart — it only
  // guarantees the axis is a date axis, with the year-less points landing in
  // the 2000s. Pin whenever at least one year-less value is on such an axis.
  return yearless > 0 && yearless + dated > numeric * 2;
}

export function hasYearlessDateLabels(data: unknown): boolean {
  if (!Array.isArray(data)) return false;
  return data.some(trace => {
    // Only traces drawn on the primary x axis: callers pin `layout.xaxis`, so a
    // trace bound to `x2` must not decide that axis' type.
    const axis = (trace as { xaxis?: unknown })?.xaxis;
    if (axis !== undefined && axis !== null && axis !== "x") return false;
    const values = (trace as { x?: unknown })?.x;
    return Array.isArray(values) && values.length > 0 && hasYearlessDateAxis(values);
  });
}
